import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Breadcrumb } from "../Breadcrumb";
import { Save, X } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import API from "../api/api01";

interface AddEditUserProps {
    mode?: "create" | "edit";
    user?: {
        id?: string;
        name: string;
        email: string;
        role: string;
        phone?: string;
        address?: string;
        // Optional specific fields
        course?: string;
        year_of_academy?: string;
        admin_role?: string;
        salary_package?: string;
    };
    currentUserRole?: string; // To check if the logged in user is Admin viewing a Teacher
    onNavigate: (page: string) => void;
}

export function AddEditUser({ mode = "edit", user, currentUserRole = "Admin", onNavigate }: AddEditUserProps) {
    const [departments, setDepartments] = useState<{ id: string; name: string }[]>([]);
    const [courses, setCourses] = useState<{ id: string; name: string; department_id: string }[]>([]);

    const [formData, setFormData] = useState({
        name: user?.name || "",
        email: user?.email || "",
        password: "",
        phone: user?.phone || "",
        address: user?.address || "",
        role: user?.role || "Student",
        department_id: "",
        course_id: "",
        year_of_academy: user?.year_of_academy || "",
        admin_role: user?.admin_role || "",
        salary_package: user?.salary_package || "",
    });

    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const [deptRes, courseRes] = await Promise.all([
                    API.get("/superadmin/departments"),
                    API.get("/superadmin/courses")
                ]);
                setDepartments(deptRes.data);
                setCourses(courseRes.data);

                // If editing and we have a way to pre-fill department_id and course_id here we would
            } catch (error) {
                toast.error("Failed to load departments or courses.");
            }
        };
        fetchOptions();
    }, []);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const payload: any = { ...formData };
            if (!payload.department_id) payload.department_id = null;
            if (!payload.course_id) payload.course_id = null;
            if (payload.password === "") delete payload.password;

            if (mode === "create") {
                await API.post("/superadmin/users", payload);
                toast.success("Profile created successfully!");
            } else {
                if (!user?.id) throw new Error("Missing target user ID!");
                await API.put(`/superadmin/profile/${user.id}`, payload);
                toast.success("Profile updated successfully!");
            }
            setTimeout(() => {
                onNavigate("view-profile");
            }, 1000);
        } catch (error: any) {
            console.error(error);
            const msg = error.response?.data?.detail || "Failed to save user data.";
            toast.error(msg);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-[#0a0a0a] p-6">
            <div className="max-w-4xl mx-auto space-y-6">
                {/* Header */}
                <div>
                    <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
                        {mode === "create" ? "Create Profile" : "Edit Profile"}
                    </h1>
                    <Breadcrumb
                        items={[{ label: "Profile" }, { label: mode === "create" ? "Create Profile" : "Edit Profile" }]}
                        className="mt-2"
                    />
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit}>
                    <Card className="bg-white dark:bg-[#141414]">
                        <CardHeader>
                            <CardTitle>
                                {mode === "create" ? "Add New User" : "Personal Information"}
                            </CardTitle>
                            {mode === "create" && (
                                <p className="text-sm text-muted-foreground">Create a new user account</p>
                            )}
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {mode === "create" && (
                                    <div className="space-y-2 md:col-span-2">
                                        <Label htmlFor="role">Role</Label>
                                        <Select
                                            value={formData.role}
                                            onValueChange={(value) => setFormData({ ...formData, role: value })}
                                        >
                                            <SelectTrigger id="role">
                                                <SelectValue placeholder="Select role" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="Student">Student</SelectItem>
                                                <SelectItem value="Teacher">Teacher</SelectItem>
                                                <SelectItem value="Admin">Admin</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}

                                <div className="space-y-2">
                                    <Label htmlFor="name">Full Name</Label>
                                    <Input
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder="Enter full name"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="email">Email Address</Label>
                                    <Input
                                        id="email"
                                        name="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="Enter email"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="phone">Phone Number</Label>
                                    <Input
                                        id="phone"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        placeholder="Enter phone number"
                                    />
                                </div>

                                {(formData.role === "Student" || formData.role === "Teacher") && (
                                    <div className="space-y-2 md:col-span-2">
                                        <Label htmlFor="department_id">Department</Label>
                                        <Select
                                            value={formData.department_id}
                                            onValueChange={(value) => setFormData({ ...formData, department_id: value, course_id: "" })}
                                        >
                                            <SelectTrigger id="department_id">
                                                <SelectValue placeholder="Select department" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {departments.map(dept => (
                                                    <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}

                                {/* Role Specific Fields */}
                                {(formData.role === "Student" || formData.role === "Teacher") && (
                                    <>
                                        <div className="space-y-2">
                                            <Label htmlFor="course_id">Course</Label>
                                            <Select
                                                value={formData.course_id}
                                                onValueChange={(value) => setFormData({ ...formData, course_id: value })}
                                                disabled={!formData.department_id}
                                            >
                                                <SelectTrigger id="course_id">
                                                    <SelectValue placeholder={formData.department_id ? "Select course" : "Select department first"} />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {courses.filter(c => c.department_id === formData.department_id).map(c => (
                                                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </>
                                )}

                                {formData.role === "Student" && (
                                    <div className="space-y-2">
                                        <Label htmlFor="year_of_academy">Year of Academy</Label>
                                        <Select
                                            value={formData.year_of_academy}
                                            onValueChange={(value) => setFormData({ ...formData, year_of_academy: value })}
                                        >
                                            <SelectTrigger id="year_of_academy">
                                                <SelectValue placeholder="Select year" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="2024-2028">2024-2028</SelectItem>
                                                <SelectItem value="2023-2027">2023-2027</SelectItem>
                                                <SelectItem value="2022-2026">2022-2026</SelectItem>
                                                <SelectItem value="2021-2025">2021-2025</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}

                                {formData.role === "Admin" && (
                                    <div className="space-y-2">
                                        <Label htmlFor="admin_role">Admin Role</Label>
                                        <Select
                                            value={formData.admin_role}
                                            onValueChange={(value) => setFormData({ ...formData, admin_role: value })}
                                        >
                                            <SelectTrigger id="admin_role">
                                                <SelectValue placeholder="Select admin role" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="Salary Manager">Salary Manager</SelectItem>
                                                <SelectItem value="Exam Controller">Exam Controller</SelectItem>
                                                <SelectItem value="Data Entry">Data Entry</SelectItem>
                                                <SelectItem value="Super Admin">Super Admin</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}

                                {formData.role === "Teacher" && currentUserRole === "Admin" && (
                                    <div className="space-y-2">
                                        <Label htmlFor="salary_package">Salary Package (Admin Only)</Label>
                                        <Input
                                            id="salary_package"
                                            name="salary_package"
                                            value={formData.salary_package}
                                            onChange={handleChange}
                                            placeholder="e.g. ₹8,00,000"
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="password">Password {mode === "edit" ? "(Optional Update)" : ""}</Label>
                                <Input
                                    id="password"
                                    name="password"
                                    type="text"
                                    value={formData.password}
                                    onChange={handleChange}
                                    placeholder={mode === "edit" ? "Leave blank to keep current password" : "Enter password or use default (Welcome@123)"}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="address">Address</Label>
                                <Textarea
                                    id="address"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleChange}
                                    placeholder="Enter address"
                                    rows={3}
                                />
                            </div>
                        </CardContent>
                    </Card>

                    {/* Actions */}
                    <div className="flex gap-4 mt-6">
                        <Button type="submit" className="bg-primary hover:bg-primary/90 text-white">
                            <Save className="h-4 w-4 mr-2" />
                            Save Changes
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onNavigate("view-profile")}
                        >
                            <X className="h-4 w-4 mr-2" />
                            Cancel
                        </Button>
                    </div>
                </form>

                {/* Deprecated User Table Removed Here */}
            </div>
        </div>
    );
}
